import { useApolloClient } from "@apollo/client";
import { ErrorMessage, Modal } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, FormikProps } from "formik";
import { uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import { useResendOrganisationMembershipInvitationEmailMutation } from "./ResendOrganisationMembershipInvitationEmailModal.generated.js";
import { type OrganisationMembershipInvitationEmailFormValues } from "./useOrganisationMembershipInvitation.js";
import {
  type MemberInvitee,
  useOrganisationMembershipInvitationEmailForm,
} from "./useOrganisationMembershipInvitationEmailForm.js";

export type { OrganisationMembershipInvitationEmailFormValues } from "./useOrganisationMembershipInvitation.js";

interface Props {
  title?: string;
  handleClose: () => void;
  handleComplete?: () => Promise<void>;
  refetchQueries: string[];
  invitationId: string;
  invitee: MemberInvitee;
}

export const ResendOrganisationMembershipInvitationEmailModal = ({
  title,
  handleClose,
  handleComplete,
  refetchQueries,
  invitationId,
  invitee,
}: Props) => {
  const { t } = useTranslate(["OrganisationInvite", "UserProfile", "Global"]);
  const { enqueueSnackbar } = useSnackbar();

  const {
    initialValues,
    validationSchema,
    formFields,
    templatesLoading,
    templatesError,
  } = useOrganisationMembershipInvitationEmailForm({ invitee });

  const client = useApolloClient();
  const [resendInvite, { loading: isLoading }] =
    useResendOrganisationMembershipInvitationEmailMutation({
      client,
      refetchQueries,
      awaitRefetchQueries: true,
    });

  const onSubmit = React.useCallback(
    async (values: OrganisationMembershipInvitationEmailFormValues) => {
      try {
        await resendInvite({
          variables: {
            input: {
              organisationMembershipInvitationId: invitationId,
              messageSubject: values.subject,
              messageBody: values.message,
            },
          },
        });
        if (handleComplete) {
          await handleComplete();
        }
        enqueueSnackbar(t("Member invited", { ns: "OrganisationInvite" }), {
          variant: "success",
        });
      } catch (error) {
        enqueueSnackbar(
          error instanceof Error ? error.message : t("Error", { ns: "Global" }),
          {
            variant: "error",
          }
        );
      } finally {
        handleClose();
      }
    },
    [
      enqueueSnackbar,
      handleClose,
      handleComplete,
      invitationId,
      resendInvite,
      t,
    ]
  );

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <Formik<OrganisationMembershipInvitationEmailFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {(
        formikProps: FormikProps<OrganisationMembershipInvitationEmailFormValues>
      ) => (
        <Modal
          id="reinvite-ogranisation-member-modal"
          title={
            title ??
            t("Resend invitation email", {
              ns: "OrganisationInvite",
            })
          }
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Cancel", {
                ns: "Global",
              }),
              handleClick: handleClose,
              buttonProps: { variant: "text" },
            },
            {
              label: t("Invite", {
                ns: "Global",
              }),
              buttonProps: {
                type: "submit",
                form: formId,
                disabled:
                  !formikProps.isValid ||
                  templatesLoading ||
                  Boolean(templatesError),
                loading: formikProps.isSubmitting,
              },
            },
          ]}
          isLoading={isLoading || templatesLoading}
        >
          {templatesError ? (
            <ErrorMessage message={templatesError} />
          ) : (
            <Form id={formId}>{formFields}</Form>
          )}
        </Modal>
      )}
    </Formik>
  );
};
